import type { SoftphoneActionButtonProps } from './index.types';
import {
  ShortcutTooltipContent,
  reportShortcutTooltipViewed,
} from 'Modules/softphone/components/SoftphoneShortcutTooltip';
import { ANALYTICS_EVENT_SOFTPHONE } from 'Modules/softphone/constants/analytics';
import { getShortcutAriaLabel } from 'Modules/softphone/constants/keyboardShortcuts';
import { TOOLTIP_DELAY_MS } from 'Modules/softphone/constants/timing';
import {
  getCallActionCopy,
  hasStateVaryingCaption,
} from 'Modules/softphone/utils/callActionCopy';
import React, { useId } from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { IconButton } from '~/lib/ui/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/lib/ui/tooltip';

/**
 * An {@link IconButton} for a softphone call action, with its copy resolved
 * from CALL_ACTION_COPY:
 *
 * - available → caption + a tooltip with the shortcut chips, and an accessible
 *   name that announces the shortcut too;
 * - disabled → the same caption, the shortcut dropped (the keydown handler is
 *   gated by the same permissions, so the chord would do nothing), and the
 *   reason exposed via `aria-describedby`. `aria-disabled` carries the fact it
 *   cannot run; the name is not made to repeat it.
 *
 * Unavailable means `aria-disabled`, not the native attribute, so the control
 * stays reachable and can say why it is off.
 *
 * `label` and `ariaLabel` stay available as overrides for what the table can't
 * know, e.g. the incoming-call banner naming the caller.
 */
export const SoftphoneActionButton = ({
  action,
  unavailableReason,
  isActive,
  copy,
  ...buttonProps
}: SoftphoneActionButtonProps): React.ReactElement => {
  const descriptionId = useId();

  // Toggled-on styling is the same for every action, so the call sites only
  // declare `isPressed` and can still override either piece when they differ
  // (the destructive Hang up button).
  const pressedStyling = {
    variant:
      buttonProps.variant ?? (buttonProps.isPressed ? 'secondary' : 'outline'),
    iconClassName:
      buttonProps.iconClassName ??
      (buttonProps.isPressed ? 'brightness-0 invert' : ''),
  } as const;

  if (!action) {
    return <IconButton {...buttonProps} {...pressedStyling} />;
  }

  const isDisabled = Boolean(buttonProps.disabled);
  const isToggledOn = Boolean(isActive ?? buttonProps.isPressed);
  const { caption, tooltip } = getCallActionCopy(action, {
    isActive: isToggledOn,
    isDisabled,
    reason: unavailableReason,
    override: copy,
  });

  const label = buttonProps.label ?? caption;

  // Running, not unavailable: `aria-disabled` on the focused control is read
  // back as "unavailable". `disabled` while toggled on is the older spelling.
  const isBusy = Boolean(buttonProps.busy) || (isDisabled && isToggledOn);

  // The caption the action carries when nothing is in flight.
  const { caption: steadyCaption } = getCallActionCopy(action, {
    override: copy,
  });

  // The visible caption (WCAG 2.5.3 label-in-name) plus either the shortcut or
  // the fact the action can't run — never both.
  //
  // Mid-flight the name stays on the steady caption. Renaming a control that
  // has focus makes the reader speak the new name, and those are not the words
  // this step owns — the live region narrates what is happening. A caller that
  // passes its own `label` still wins: that label is what is on screen.
  const baseName =
    buttonProps.ariaLabel ??
    buttonProps.label ??
    (isBusy ? steadyCaption : caption);

  // Each piece is carried by the attribute meant for it, so a screen reader
  // says every one of them exactly once: the name names, `aria-disabled` says
  // it cannot run, and the description says why. Putting "unavailable" or the
  // reason in the name too is what makes a reader repeat itself.
  //
  // A control that cannot be activated drops the chord — the keydown handler is
  // gated by the same permission, so advertising it would be a lie.
  const accessibleName =
    isDisabled || isBusy ? baseName : getShortcutAriaLabel(baseName, action);

  // While disabled the tooltip already resolves to the reason. Mid-flight there
  // is no reason to give — the live region narrates the step — so describing
  // the button would only add a second voice saying the same thing.
  const description =
    isDisabled && !isBusy && tooltip !== baseName ? tooltip : undefined;

  // An action that renames itself when toggled must not also be `aria-pressed`,
  // or a muted mic announces as "Unmute, pressed". The styling stays.
  const isPressed = hasStateVaryingCaption(action, copy)
    ? undefined
    : buttonProps.isPressed;

  // Focus reaching an `aria-disabled` control is the observable half of it
  // being announced as unavailable; the speaking happens out of the page's
  // reach.
  const reportDisabledControlReached = (): void => {
    pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.SR_DISABLED_CONTROL_ANNOUNCED, {
      action,
      reason: description,
    });
  };

  const button = (
    <>
      <IconButton
        {...buttonProps}
        {...pressedStyling}
        label={label}
        ariaLabel={accessibleName}
        onFocus={isDisabled ? reportDisabledControlReached : undefined}
        isPressed={isPressed}
        keepFocusableWhenDisabled
        ariaDescribedBy={description ? descriptionId : undefined}
      />
      {description && (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      )}
    </>
  );

  // The chips while the action can run, the reason while it cannot.
  const tooltipContent = isDisabled || isBusy ? (
    tooltip && <p className="text-[12px] whitespace-pre-line">{tooltip}</p>
  ) : (
    <ShortcutTooltipContent action={action} label={tooltip ?? label} />
  );

  // One tree in every state, on purpose. Returning a different wrapper once
  // `disabled` flips makes React unmount the subtree and build a new `<button>`,
  // and the browser drops focus to `<body>` — where a screen reader has no
  // position, reads the page from the top, and skips the polite announcement
  // the action was about to make. Only the content inside changes.
  return (
    <Tooltip
      delayDuration={TOOLTIP_DELAY_MS}
      onOpenChange={
        isDisabled || isBusy
          ? undefined
          : (open) => reportShortcutTooltipViewed(action, open)
      }
    >
      <TooltipTrigger asChild>
        <div>{button}</div>
      </TooltipTrigger>
      {tooltipContent && (
        <TooltipContent side="bottom" sideOffset={-19}>
          {tooltipContent}
        </TooltipContent>
      )}
    </Tooltip>
  );
};
